/**
 * Loading Overlay Component
 *
 * AI 응답 대기 중 사용자에게 단계별 피드백을 제공하는 로딩 오버레이
 */
import { Brain, MessageCircle, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

interface LoadingOverlayProps {
  isVisible: boolean;
  type?: "evaluation" | "chat" | "general";
}

const loadingSteps = {
  evaluation: [
    { text: "AI 동생이 설명을 읽고 있어요...", icon: Brain, duration: 2000 },
    { text: "꼼꼼하게 평가하고 있어요...", icon: Sparkles, duration: 3000 },
    {
      text: "피드백을 준비하고 있어요...",
      icon: MessageCircle,
      duration: 2000,
    },
  ],
  chat: [
    {
      text: "AI 동생이 질문을 생각하고 있어요...",
      icon: Brain,
      duration: 2000,
    },
    { text: "답변을 준비하고 있어요...", icon: MessageCircle, duration: 3000 },
  ],
  general: [{ text: "처리 중입니다...", icon: Sparkles, duration: 2000 }],
};

export function LoadingOverlay({
  isVisible,
  type = "evaluation",
}: LoadingOverlayProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const steps = loadingSteps[type];

  useEffect(() => {
    if (!isVisible) {
      setCurrentStep(0);
      return;
    }

    let currentIndex = 0;
    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % steps.length;
      setCurrentStep(currentIndex);
    }, steps[currentIndex].duration);

    return () => clearInterval(interval);
  }, [isVisible, steps]);

  if (!isVisible) return null;

  const CurrentIcon = steps[currentStep].icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative flex flex-col items-center gap-6 rounded-2xl bg-card p-8 shadow-2xl border border-border">
        {/* Animated Icon */}
        <div className="relative">
          {/* Pulsing rings */}
          <div className="absolute inset-0 animate-ping rounded-full bg-primary opacity-20" />
          <div className="absolute inset-2 animate-pulse rounded-full bg-primary opacity-30" />

          {/* Icon */}
          <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-primary">
            <CurrentIcon className="h-10 w-10 animate-bounce text-primary-foreground" />
          </div>
        </div>

        {/* Loading Text */}
        <div className="text-center">
          <p className="animate-pulse text-lg font-semibold text-foreground">
            {steps[currentStep].text}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            잠시만 기다려주세요 ⏳
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-64">
          <div className="h-2 overflow-hidden rounded-full bg-secondary">
            <div
              className="animate-progress h-full bg-primary transition-all duration-1000"
              style={{
                width: `${((currentStep + 1) / steps.length) * 100}%`,
              }}
            />
          </div>
          <div className="mt-2 flex justify-between text-xs text-muted-foreground">
            <span>단계 {currentStep + 1}</span>
            <span>{steps.length}단계 중</span>
          </div>
        </div>

        {/* Animated dots */}
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-2 w-2 animate-bounce rounded-full bg-primary"
              style={{
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes progress {
          0% {
            width: 0%;
          }
          100% {
            width: 100%;
          }
        }

        .animate-progress {
          animation: progress 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
